import { AccountAccount, CircleCircle } from '@calm/evexia-web-sdk';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { CIRCLE_REFRESH_TOKEN_TTL_MS } from '@rhg/utils/circle';
import useAccount from '../use-account';

// 401 handling lives in the shared axios client — any unauthorized response
// redirects to the logout page. See @rhg/libs/api/client/unauthorized.
const circlesQueryOptions = {
    queryKey: () => [queryKeys.circles],
    queryFn: async ({
        getAccessTokenSilently,
        rHGApp,
        forceRefreshSponsor = false,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        rHGApp: string;
        forceRefreshSponsor?: boolean;
    }) => {
        const api = createCircleApi(getAccessTokenSilently);
        const response = await api.getCirclesV1({
            rHGApp,
            forceRefreshSponsor: forceRefreshSponsor ? 'true' : undefined,
        });
        const circles = response.data;

        circles.forEach((circle) =>
            queryClient.setQueryData<CircleCircle>(
                [queryKeys.circleById, circle.recipient.id],
                circle
            )
        );

        return circles;
    },
    queryOptions: ({
        isAuthenticated,
        account,
    }: {
        isAuthenticated: boolean;
        account?: AccountAccount;
    }) => ({
        enabled: !!(isAuthenticated && account?.userId),
        staleTime: CIRCLE_REFRESH_TOKEN_TTL_MS,
    }),
};

export const useCircles = () => {
    const { queryKey, queryFn, queryOptions } = circlesQueryOptions;
    const rHGApp = getRhgAppHeader();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { account } = useAccount();
    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(),
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                rHGApp,
            }),
        ...queryOptions({ isAuthenticated, account }),
    });

    return {
        circles: data,
        hasCircles: data ? data?.length > 0 : false,
        ...otherProps,
    };
};

export function useGetCircles() {
    const { queryFn } = circlesQueryOptions;
    const rHGApp = getRhgAppHeader();
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: (forceRefreshSponsor: boolean = false) =>
            queryFn({
                forceRefreshSponsor,
                rHGApp,
                getAccessTokenSilently,
            }),
    });
    return { getCircles: mutate, getCirclesAsync: mutateAsync, ...otherProps };
}

export default useCircles;
